/**
 * IssueFormDialog — bám Access spec `frmFactoryQryIssueAdd`
 * (`plan/access_all/form_frmFactoryQryIssueAdd.txt`).
 *
 * Layout (Access Left/Top coords):
 *   Left col (input Left=2496):
 *     stack_id (Top=480)
 *     txt_balance_bag (Top=840, readonly Format=Standard)
 *     txt_balance_nw  (Top=1200, readonly)
 *     stack_quality   (Top=1560, readonly DLookUp)
 *     date            (Top=1920)
 *     po_no           (Top=2280)
 *     issued_bag      (Top=2640)
 *     issued_nw       (Top=3000)
 *   Right col (input Left=9216):
 *     note (Top=480, Height=1728 — spans rows 1-5)
 *     mc_in (Top=2280)
 *     mc_out (Top=2640)
 *     factory_net_weight (Top=3000)
 *
 * Required (line 2324): `strCheck = ",po_no,stack_id,issued_bag,issued_nw,"`
 * Validate (lines 2328 / 2339): issued_bag <= txt_balance_bag, issued_nw <= txt_balance_nw.
 * stack_id AfterUpdate/Click (lines 2354/2364) → recompute snapshot via BE.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import {
  useGetIssueComboOptionsQuery,
  useLazyGetIssueStackSnapshotQuery,
  type FactoryBRowPayload,
  type FactoryCell,
} from '@/store/api/factoryApi'
import { FORM_GRID_2COL, HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

type IssueRow = { id: string | number } & Record<string, FactoryCell>

interface DraftState {
  stack_id: string
  date: string
  po_no: string
  issued_bag: string
  issued_nw: string
  mc_in: string
  mc_out: string
  factory_net_weight: string
  note: string
}

const EMPTY_DRAFT: DraftState = {
  stack_id: '',
  date: '',
  po_no: '',
  issued_bag: '',
  issued_nw: '',
  mc_in: '',
  mc_out: '',
  factory_net_weight: '',
  note: '',
}

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Bg cho field `Enabled = NotDefault` trong Access (txt_balance_bag,
// txt_balance_nw, stack_quality) — phân biệt readonly vs editable.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: (theme: { palette: { text: { secondary: string } } }) =>
      theme.palette.text.secondary,
    fontWeight: 600,
  },
}

// Access `Format = "Standard"` = `#,##0.00` (thousand sep + 2 decimals).
const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

// Parse user input ("1,234.5" / "1234.5" / "") → number | null.
function parseNumeric(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

// Format raw value → Standard string (`1,234.56` / `''`).
function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

interface IssueFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  row: IssueRow | null
  saving: boolean
  factoryCode: string
  /** Permission gating — false → ẩn Save + show read-only banner (layer 4). */
  canEdit?: boolean
  onClose: () => void
  onSave: (payload: FactoryBRowPayload) => void
}

export function IssueFormDialog({
  open,
  mode,
  row,
  saving,
  factoryCode,
  canEdit = true,
  onClose,
  onSave,
}: IssueFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [balanceBag, setBalanceBag] = useState<number | null>(null)
  const [balanceNw, setBalanceNw] = useState<number | null>(null)
  const [stackQuality, setStackQuality] = useState<string | null>(null)
  const [snapshotError, setSnapshotError] = useState('')

  const [triggerSnapshot, { isFetching: snapshotLoading }] =
    useLazyGetIssueStackSnapshotQuery()

  const issueId = useMemo(() => {
    if (mode !== 'edit' || !row) return null
    const v = row.id
    const n = typeof v === 'number' ? v : parseInt(String(v), 10)
    return Number.isFinite(n) ? n : null
  }, [mode, row])

  // Hydrate draft khi dialog mở. New → empty (date = today). Edit → row values.
  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && row) {
      setDraft({
        stack_id: String(row.stack_id ?? ''),
        date: String(row.date ?? '').slice(0, 10),
        po_no: String(row.po_no ?? ''),
        // Pre-format numeric với Standard `#,##0.00` để Edit mode đồng nhất
        // với UI readonly snapshot. User typing sẽ auto-format lại trên blur.
        issued_bag: formatStandard(row.issued_bag as number | string | null),
        issued_nw: formatStandard(row.issued_nw as number | string | null),
        mc_in: formatStandard(row.mc_in as number | string | null),
        mc_out: formatStandard(row.mc_out as number | string | null),
        factory_net_weight: formatStandard(
          row.factory_net_weight as number | string | null,
        ),
        note: String(row.note ?? ''),
      })
    } else {
      setDraft({
        ...EMPTY_DRAFT,
        date: new Date().toISOString().slice(0, 10),
      })
    }
    setBalanceBag(null)
    setBalanceNw(null)
    setStackQuality(null)
    setSnapshotError('')
  }, [open, mode, row])

  // Mirror Access `stack_id_AfterUpdate` / `_Click`: refetch snapshot khi
  // stack_id đổi. Edit-mode truyền issueId để BE restore issued_bag/_nw của
  // chính row đang edit (cho phép user re-allocate full quota).
  useEffect(() => {
    if (!open) return
    const sid = draft.stack_id.trim()
    if (!sid) {
      setBalanceBag(null)
      setBalanceNw(null)
      setStackQuality(null)
      setSnapshotError('')
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const res = await triggerSnapshot({
          factoryCode,
          stackId: sid,
          issueId: mode === 'edit' ? issueId : null,
        }).unwrap()
        if (cancelled) return
        setBalanceBag(res.txt_balance_bag)
        setBalanceNw(res.txt_balance_nw)
        setStackQuality(res.stack_quality)
        setSnapshotError('')
        // Enhancement (New mode only): auto-suggest issued_bag/issued_nw =
        // balance available — UX "issue full quota". User chỉnh tay nếu partial.
        // Edit mode KHÔNG suggest vì sẽ overwrite giá trị đang lưu.
        if (mode === 'new') {
          setDraft((prev) => ({
            ...prev,
            issued_bag: formatStandard(res.txt_balance_bag),
            issued_nw: formatStandard(res.txt_balance_nw),
          }))
        }
      } catch {
        if (cancelled) return
        setBalanceBag(null)
        setBalanceNw(null)
        setStackQuality(null)
        setSnapshotError('Stack not found in current factory')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, draft.stack_id, factoryCode, issueId, mode, triggerSnapshot])

  // Combo RowSource bám Access frmFactoryQryIssueAdd Form_Load:
  //   po_no    = po WHERE is_finished=false (active), order id desc
  //   stack_id = stack, order id desc
  // (KHÔNG phải distinct từ list đang xem — đó là sai so với Access.)
  const { data: comboOptions } = useGetIssueComboOptionsQuery(
    { factoryCode },
    { skip: !open || !factoryCode },
  )
  // Edit: inject value đang lưu nếu nằm ngoài RowSource (vd PO đã Finished) để
  // Autocomplete không hiện blank ([[autocomplete_inject_current_value]]).
  const stackIdOptions = useMemo(() => {
    const base = comboOptions?.stack_ids ?? []
    const cur = draft.stack_id.trim()
    return cur && !base.includes(cur) ? [cur, ...base] : base
  }, [comboOptions, draft.stack_id])
  // po_no options = {po_no, po_date}; label dropdown "<po_no> — <po_date>" (Access
  // ColumnCount=2), value lưu = po_no. Inject value đang lưu nếu ngoài RowSource.
  const poNoOptions = useMemo<{ po_no: string; po_date: string | null }[]>(() => {
    const base = comboOptions?.po_nos ?? []
    const cur = draft.po_no.trim()
    return cur && !base.some((o) => o.po_no === cur)
      ? [{ po_no: cur, po_date: null }, ...base]
      : base
  }, [comboOptions, draft.po_no])

  const stackIdEmpty = draft.stack_id.trim().length === 0
  const poNoEmpty = draft.po_no.trim().length === 0
  // parseNumeric strip thousand-sep commas (Standard format) trước khi check.
  const issuedBagNum = parseNumeric(draft.issued_bag)
  const issuedNwNum = parseNumeric(draft.issued_nw)
  const issuedBagEmpty = issuedBagNum == null
  const issuedNwEmpty = issuedNwNum == null
  const issuedBagOver =
    issuedBagNum != null && balanceBag != null && issuedBagNum > balanceBag
  const issuedNwOver =
    issuedNwNum != null && balanceNw != null && issuedNwNum > balanceNw

  const canSave =
    canEdit &&
    !stackIdEmpty &&
    !poNoEmpty &&
    !issuedBagEmpty &&
    !issuedNwEmpty &&
    !issuedBagOver &&
    !issuedNwOver &&
    !saving

  const handleChange = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  // Map btnSave_Click (line 2237): If isValid(Me,strCheck) Then Update_Insert(...)
  //   → parent (FactoryPage) sẽ invalidate cache khi onSave resolve === Form.Requery.
  const handleSave = () => {
    if (!canSave) return
    const str = (v: string): string | null => {
      const s = v.trim()
      return s.length ? s : null
    }
    const payload: FactoryBRowPayload = {
      stack_id: draft.stack_id.trim(),
      date: str(draft.date),
      po_no: draft.po_no.trim(),
      // parseNumeric strip commas — payload BE nhận raw number.
      issued_bag: parseNumeric(draft.issued_bag),
      issued_nw: parseNumeric(draft.issued_nw),
      mc_in: parseNumeric(draft.mc_in),
      mc_out: parseNumeric(draft.mc_out),
      factory_net_weight: parseNumeric(draft.factory_net_weight),
      note: str(draft.note),
    }
    onSave(payload)
  }

  // Auto-format value sang Standard `#,##0.00` khi user blur khỏi field số.
  const handleNumericBlur = <K extends keyof DraftState>(key: K) => () => {
    const raw = draft[key]
    const formatted = formatStandard(raw)
    if (formatted !== raw) setDraft((prev) => ({ ...prev, [key]: formatted }))
  }

  // Map btnCancel_Click (line 2230): Me.Undo + DoCmd.Close + parent Requery.
  //   Undo: draft là local state → dialog unmount tự xoá.
  //   Close: parent setPendingAction(null).
  //   Requery: KHÔNG cần — list chưa thay đổi vì user cancel.
  //   Race: block close khi mutation đang fly (saving=true).
  const handleClose = () => {
    if (saving) return
    onClose()
  }

  // Access Format = "Standard" → `#,##0.00` (en-US locale).
  const formatNumber = (n: number | null): string =>
    n == null
      ? '—'
      : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  return (
    <Dialog
      open={open}
      // Esc / backdrop cũng route qua handleClose để block khi saving.
      onClose={(_event, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return // accident-proof; user phải bấm Cancel
        handleClose()
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(720px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          noWrap
          title={`${mode === 'new' ? 'New' : 'Edit'} — Factory · Issue`}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {mode === 'new' ? 'New' : 'Edit'} —
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {' Factory ·'}
          </Box>
          {' Issue'}
        </Typography>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.5 }}
        >
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          {/* btnSave (Access line 511, FontWeight=700) — primary action. */}
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canSave}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          {/* btnCancel (Access line 560). Block khi saving — không cho user
             close giữa lúc mutation đang chạy. */}
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          ...compactInputSx,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          pt: 1.5,
        }}
      >
        {!canEdit ? (
          <Alert severity="info" sx={{ mb: 1.25, py: 0.25 }}>
            Read-only — you don't have edit permission for this factory.
          </Alert>
        ) : null}
        {snapshotError ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            {snapshotError}
          </Alert>
        ) : null}

        {/* 2-col grid — sắp lại theo yêu cầu chị Đẹp 2026-06-26 (gom field cùng
            cặp 1 dòng), KHÔNG còn 1:1 Access Top coord:
            Row 1: stack_id          | note (spans 1-4)
            Row 2: balance_bag       |
            Row 3: balance_nw        |
            Row 4: stack_quality     |
            Row 5: date              | po_no
            Row 6: issued_bag        | issued_nw
            Row 7: mc_in             | mc_out
            Row 8: factory_net_weight (full hàng) */}
        <Box sx={FORM_GRID_2COL}>
          {/* Row 1 — stack_id | note (note spans 5 rows).
             Edit mode disable Stack ID: đổi stack giữa chừng sẽ phá snapshot
             (balance lookup add-back issueId) và làm lệch FK với row đang edit. */}
          {/* ComboBox (Access RowSourceType=Table/Query) — dropdown chọn từ list,
             KHÔNG freeSolo. Inject value đang lưu (Edit) qua stackIdOptions. */}
          <Autocomplete
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 } }}
            size="small"
            disabled={mode === 'edit'}
            options={stackIdOptions}
            value={draft.stack_id || null}
            onChange={(_, v) => handleChange('stack_id', v ?? '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Stack ID"
                required
                fullWidth
                error={!stackIdEmpty && snapshotError !== ''}
                autoFocus={mode === 'new'}
                helperText={mode === 'edit' ? 'Cannot be changed after creation.' : undefined}
                sx={mode === 'edit' ? readonlyFieldSx : undefined}
                inputProps={{ ...params.inputProps, tabIndex: 1 }}
              />
            )}
          />
          <TextField
            sx={{
              gridColumn: { xs: 1, sm: 2 },
              gridRow: { sm: '1 / span 4' },
              // Grid container alignItems:'start' (FORM_GRID_2COL) → item không tự
              // stretch theo row-span; ép alignSelf+height 100% để Note cao bằng
              // khối Stack 4 hàng bên trái.
              alignSelf: 'stretch',
              height: '100%',
              '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
              '& .MuiInputBase-inputMultiline': { height: '100% !important', overflow: 'auto' },
            }}
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={4}
            value={draft.note}
            onChange={(e) => handleChange('note', e.target.value)}
            inputProps={{ tabIndex: 9 }}
            InputProps={{ sx: { height: '100%', alignItems: 'stretch' } }}
          />

          {/* Row 2 — balance_bag (left only) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 2 }, ...readonlyFieldSx }}
            label="Stack - Balance Bag"
            size="small"
            fullWidth
            value={formatNumber(balanceBag)}
            InputProps={{
              readOnly: true,
              endAdornment: snapshotLoading ? <CircularProgress size={14} /> : null,
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ ...numericInputProps, tabIndex: -1 }}
          />

          {/* Row 3 — balance_nw (left only) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 3 }, ...readonlyFieldSx }}
            label="Stack - Balance NW"
            size="small"
            fullWidth
            value={formatNumber(balanceNw)}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ ...numericInputProps, tabIndex: -1 }}
          />

          {/* Row 4 — stack_quality (left only) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 4 }, ...readonlyFieldSx }}
            label="Stack - Quality"
            size="small"
            fullWidth
            value={stackQuality ?? ''}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: -1 }}
          />

          {/* Row 5 — date (left only) */}
          <DatePicker
            label="Date"
            format="DD-MMM-YYYY"
            value={draft.date ? dayjs(draft.date) : null}
            onChange={(d) =>
              handleChange('date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')
            }
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputLabelProps: { shrink: true },
                inputProps: { tabIndex: 2 },
                sx: { gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 5 } },
              },
            }}
          />

          {/* Row 5 col 2 — po_no (ngang Date). Option = {po_no, po_date}; dropdown
             hiện "<po_no> — <po_date>" (Access ColumnCount=2), input/value = po_no. */}
          {/* ComboBox (Access RowSourceType=Table/Query) — dropdown chọn từ list,
             KHÔNG freeSolo. Option {po_no, po_date}; value lưu = po_no. */}
          <Autocomplete<{ po_no: string; po_date: string | null }, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 5 } }}
            size="small"
            options={poNoOptions}
            value={poNoOptions.find((o) => o.po_no === draft.po_no) ?? null}
            onChange={(_, v) => handleChange('po_no', v?.po_no ?? '')}
            getOptionLabel={(o) => {
              const d = o.po_date ? dayjs(o.po_date).format('DD-MMM-YYYY') : ''
              return d ? `${o.po_no} — ${d}` : o.po_no
            }}
            isOptionEqualToValue={(a, b) => a.po_no === b.po_no}
            renderInput={(params) => (
              <TextField
                {...params}
                label="PO No"
                required
                fullWidth
                inputProps={{ ...params.inputProps, tabIndex: 3 }}
              />
            )}
          />

          {/* Row 6 — issued_bag | issued_nw */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 6 } }}
            label="Issued Bag"
            size="small"
            fullWidth
            required
            type="text"
            value={draft.issued_bag}
            onChange={(e) => handleChange('issued_bag', e.target.value)}
            onBlur={handleNumericBlur('issued_bag')}
            error={issuedBagOver}
            helperText={
              issuedBagOver
                ? `Cannot exceed balance (${formatNumber(balanceBag)})`
                : undefined
            }
            inputProps={{ ...numericInputProps, tabIndex: 4 }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 6 } }}
            label="Issued Net Weight"
            size="small"
            fullWidth
            required
            type="text"
            value={draft.issued_nw}
            onChange={(e) => handleChange('issued_nw', e.target.value)}
            onBlur={handleNumericBlur('issued_nw')}
            error={issuedNwOver}
            helperText={
              issuedNwOver
                ? `Cannot exceed balance (${formatNumber(balanceNw)})`
                : undefined
            }
            inputProps={{ ...numericInputProps, tabIndex: 5 }}
          />

          {/* Row 7 — mc_in | mc_out */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 7 } }}
            label="MC In"
            size="small"
            fullWidth
            type="text"
            value={draft.mc_in}
            onChange={(e) => handleChange('mc_in', e.target.value)}
            onBlur={handleNumericBlur('mc_in')}
            inputProps={{ ...numericInputProps, tabIndex: 6 }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 7 } }}
            label="MC Out"
            size="small"
            fullWidth
            type="text"
            value={draft.mc_out}
            onChange={(e) => handleChange('mc_out', e.target.value)}
            onBlur={handleNumericBlur('mc_out')}
            inputProps={{ ...numericInputProps, tabIndex: 7 }}
          />

          {/* Row 8 — factory_net_weight (full hàng) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: '1 / -1' }, gridRow: { sm: 8 } }}
            label="Factory Net Weight"
            size="small"
            fullWidth
            type="text"
            value={draft.factory_net_weight}
            onChange={(e) => handleChange('factory_net_weight', e.target.value)}
            onBlur={handleNumericBlur('factory_net_weight')}
            inputProps={{ ...numericInputProps, tabIndex: 8 }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default IssueFormDialog
